import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import ReactRouterPropTypes from 'react-router-prop-types'
import { withRouter } from 'react-router-dom'
import styled from 'styled-components'
import _ from 'lodash'
import { isMobile } from 'react-device-detect'
import { injectIntl } from 'react-intl'

import MainContentWrapper from 'core/templates/MainContentWrapper'
import useGetCar from 'core/hooks/useGetCar'
import * as routes from 'core/routing/constants/index'
import PageLoading from 'core/components/page-loading'
import { Row, Col, Collapse, List, Checkbox, Empty, Input, Icon } from 'core/components/ui'
import wizardsShape from 'core/components/wizard/shapes'
import { font, greys, fontFamily } from 'assets/themes/theme-default'
import TitleIcon from 'core/layouts/title-icon'
import getEquipmentGroupings from 'core/utils/equipments'

import { tradeInShape } from '../shapes'
import {
  REFERENTIAL_AUTOVISTA_FR,
  REFERENTIAL_SANGUINETTI_IT,
  REFERENTIAL_JATO_ES,
  REFERENTIAL_JATO_IT,
  REFERENTIAL_DAT_DE,
  REFERENTIAL_AUTOVISTA_ES,
  REFERENTIAL_AUTOVISTA_IT,
  REFERENTIAL_AUTOVISTA_BE,
  REFERENTIAL_AUTOVISTA_PT,
  isReferentialAutovista
} from '../../core/utils/enums/referential'

const { Panel } = Collapse

const SearchWrapper = styled.div`
  margin: 0 0 25px 0;
`

const ClickableRow = styled(Row)`
  cursor: pointer;
  font-size: ${font.size.base};
  line-height: ${isMobile && 'normal'};
`
const ColDataInput = styled(Col)`
  padding-top: 2px;
`

const ColDataLabel = styled(Col)`
  color: ${greys.dark1};
`

const SubGroupLabel = styled.h4`
  color: ${greys.dark1};
  font-family: ${props => props.theme.fontFamily && `${props.theme.fontFamily.bold.name},`}
    ${fontFamily.bold};
  font-size: ${font.size.base};
  border-bottom: 1px solid ${greys.dark1};
`
const ListWrapper = styled.div`
  padding-left: ${isMobile ? '10px' : '50px'};
`

const Options = props => {
  const {
    history,
    intl,
    tradeIn,
    setTradeIn,
    setWizards,
    wizards,
    campaign,
    tradeInContext
  } = props

  const { idReferential } = campaign

  const { getOptions, carOptions, loading } = useGetCar({
    redirectMessage: intl.formatMessage({ id: 'ui-session-expired' })
  })

  const [list, setList] = useState([])

  const { vehicle, filtersList } = tradeIn
  const { optionsList } = filtersList
  const { equipmentGroups, equipments } = optionsList
  const { options } = vehicle

  const generateOptionsListAutovista = filter => {
    const filteredEquipments = equipments.filter(equipment => {
      if (equipment.serial) return false
      const labels = equipment.equipment
      const allLabels = labels.toLowerCase().split(' ')

      const isPresent = filter
        ? _.first(filter.map(fil => allLabels.filter(word => word.includes(fil)))).length > 0
        : true
      return isPresent
    })

    const isIncludedInGroup = (equipment, groupId) => {
      if (!equipment.equipmentGroups) {
        return
      }
      // eslint-disable-next-line consistent-return
      return equipment.equipmentGroups.includes(groupId)
    }

    const generatedOptionsList = equipmentGroups.map(group => {
      const subGroupsList = group.equipmentSubGroups
        .map(subGroup => {
          // Equpi
          const equipmentList = filteredEquipments.filter(equipment => {
            const currentEquipmentGroups = equipment.equipmentGroups
            const currentEquipmentSubGroups = equipment.equipmentSubGroups

            const filtredEquipmentGroups = currentEquipmentGroups.filter(g => g === group.id)
            const filtredEquipmentSubGroups = currentEquipmentSubGroups.filter(
              g => g === subGroup.id
            )

            return filtredEquipmentGroups.length > 0 && filtredEquipmentSubGroups.length > 0
          })
          if (equipmentList.length > 0) {
            return {
              label: subGroup.subGroup,
              id: subGroup.id,
              items: equipmentList
            }
          }
          return null
        })
        .filter(subgroup => subgroup)

      if (!subGroupsList.length > 0 && group.id) {
        const scopedEquipments = filteredEquipments.filter(x => isIncludedInGroup(x, group.id))
        if (scopedEquipments.length)
          return {
            label: group.group,
            id: group.id,
            items: [{ id: null, label: null, items: scopedEquipments }]
          }
        return null
      }

      return {
        label: group.group,
        id: group.id,
        items: subGroupsList
      }
    })

    return generatedOptionsList.filter(option => option)
  }

  const generateOptionsListJato = filter => {
    const filteredEquipments = equipments.filter(equipment => {
      const labels = equipment.equipment
      const allLabels = labels.toLowerCase().split(' ')

      const isPresent = filter
        ? _.first(filter.map(fil => allLabels.filter(word => word.includes(fil)))).length > 0
        : true
      return isPresent
    })
    const listPack = {
      label: 'Packs',
      id: 'packs',
      items: filteredEquipments.filter(equipement => !equipement.serial && equipement.pack)
    }

    const listOptions = {
      label: 'Options',
      id: 'options',
      items: filteredEquipments.filter(equipement => !equipement.serial && !equipement.pack)
    }

    return [listPack, listOptions].filter(option => option)
  }

  const generateOptionsList = filters => {
    const filter = filters && filters.toLowerCase().split(' ')

    switch (idReferential) {
      case REFERENTIAL_AUTOVISTA_FR:
      case REFERENTIAL_AUTOVISTA_ES:
      case REFERENTIAL_AUTOVISTA_IT:
      case REFERENTIAL_AUTOVISTA_BE:
      case REFERENTIAL_AUTOVISTA_PT:
      case REFERENTIAL_SANGUINETTI_IT:
      default:
        return generateOptionsListAutovista(filter)
      case REFERENTIAL_JATO_ES:
      case REFERENTIAL_JATO_IT:
      case REFERENTIAL_DAT_DE:
        return generateOptionsListJato(filter)
    }
  }

  const activateNextStep = () => {
    const aStepsActive = wizards[wizards.activeWizard]

    const nextIndex = aStepsActive.findIndex(step => step.hash === routes.TRADEIN_OPTIONS) + 1

    const extendedStep = { ...aStepsActive[nextIndex], isActive: true }

    const extendedSteps = Object.assign([...aStepsActive], {
      [nextIndex]: extendedStep
    })

    const extentedWizards = { ...wizards, aStepsTradeIn: extendedSteps }
    setWizards(extentedWizards)
  }

  const handleChangeSearchWord = e => {
    const { target } = e
    const { value } = target

    setList(generateOptionsList(value))
  }

  const handleOnChangeSelectionOptions = item => {
    const isSelected = !options ? false : options.filter(el => el.id === item.id).length > 0

    let extendedOptions = options ? [...options] : []

    if (!isSelected) {
      const carEquipmentGroupings = equipments.find(x => x.id === item.id)
        ? getEquipmentGroupings(
            equipmentGroups,
            equipments.find(x => x.id === item.id),
            idReferential
          )
        : []
      extendedOptions.push({ ...item, carEquipmentGroupings })
    } else {
      extendedOptions = options.filter(el => el.id !== item.id)
    }
    const extendedVehicle = { ...vehicle, options: extendedOptions }

    setTradeIn({
      ...tradeIn,
      vehicle: extendedVehicle
    })
  }

  const renderItems = items => (
    <ListWrapper>
      <List
        dataSource={items}
        renderItem={item => {
          const isChecked = !options ? false : options.filter(el => el.id === item.id).length > 0
          return (
            <List.Item>
              <ClickableRow
                type="flex"
                style={{ width: '100%' }}
                align="top"
                gutter={5}
                onClick={() => handleOnChangeSelectionOptions(item)}
              >
                <ColDataInput span={isMobile ? 3 : 1}>
                  <Checkbox checked={item.serial || isChecked} disabled={item.serial} />
                </ColDataInput>

                <ColDataLabel span={isMobile ? 21 : 23}>{item.equipment}</ColDataLabel>
              </ClickableRow>
            </List.Item>
          )
        }}
      />
    </ListWrapper>
  )

  const renderSubGroup = subGroup => {
    const { label, items, id } = subGroup
    return (
      <div key={id}>
        <SubGroupLabel>{label}</SubGroupLabel>
        {renderItems(items)}
      </div>
    )
  }

  const minusIcon = <Icon type="minus" />
  const plusIcon = <Icon type="plus" />

  const renderList = () =>
    list.map(group => {
      const { label, items, id } = group
      const textWithHyphen = label.replace(/\//g, '/\u200B')

      return (
        <Collapse
          key={id}
          bordered={false}
          expandIcon={({ isActive }) => (isActive ? minusIcon : plusIcon)}
          expandIconPosition="left"
          style={{ marginBottom: '10px' }}
        >
          <Panel header={textWithHyphen} key={1}>
            {(idReferential === REFERENTIAL_SANGUINETTI_IT ||
              isReferentialAutovista(idReferential)) &&
              items.map(subGroup => renderSubGroup(subGroup))}
            {[REFERENTIAL_JATO_ES, REFERENTIAL_JATO_IT, REFERENTIAL_DAT_DE].includes(
              idReferential
            ) && renderItems(items)}
          </Panel>
        </Collapse>
      )
    })

  useEffect(() => {
    const runIt = () => {
      const optList = generateOptionsList()
      setList(optList)
      if (!tradeInContext || !tradeInContext.loading) {
        if (options && options.length) {
          const extendedVehicle = { ...vehicle, options }
          setTradeIn({
            ...tradeIn,
            vehicle: extendedVehicle
          })
        } else if (equipments.length) {
          const serialOptions = equipments
            .filter(equipment => equipment.serial)
            .map(item => {
              const carEquipmentGroupings = equipments.find(x => x.id === item.id)
                ? getEquipmentGroupings(
                    equipmentGroups,
                    equipments.find(x => x.id === item.id),
                    idReferential
                  )
                : []
              return { ...item, carEquipmentGroupings }
            })
          const customOptions = vehicle.equipments
            ? vehicle.equipments.map(item => {
                const carEquipmentGroupings = equipments.find(x => x.id === item.id)
                  ? getEquipmentGroupings(
                      equipmentGroups,
                      equipments.find(x => x.id === item.id),
                      idReferential
                    )
                  : []
                return { ...item, carEquipmentGroupings }
              })
            : []

          const extendedVehicle = { ...vehicle, options: [...serialOptions, ...customOptions] }
          setTradeIn({
            ...tradeIn,
            vehicle: extendedVehicle
          })
        }
      }
    }

    if (equipmentGroups && equipments) runIt()
  }, [equipmentGroups, equipments])

  useEffect(() => {
    const runIt = opt => {
      const extendedFiltersList = {
        ...filtersList,
        optionsList: opt
      }

      setTradeIn({
        ...tradeIn,
        filtersList: extendedFiltersList
      })
    }
    if (carOptions) runIt(carOptions)
  }, [carOptions])

  useEffect(() => {
    // Redirection if direct access & step is not active

    const aStepsActive = wizards[wizards.activeWizard]

    const extendedStepsActive = aStepsActive.filter(step => step.hash === routes.TRADEIN_OPTIONS)
    const currentStep = extendedStepsActive[0]

    const { isActive } = currentStep

    if (!isActive) {
      history.push(routes.HOME)
    }

    if (!tradeInContext.loading) {
      const { id, wsDatas } = vehicle
      const { catalogPriceBegin } = wsDatas

      const params = { id, catalog: catalogPriceBegin }
      getOptions(params)
    }

    activateNextStep()
  }, [])

  return (
    <MainContentWrapper>
      {loading ? (
        <PageLoading />
      ) : (
        <>
          <TitleIcon
            cssId="globaldealer-title-4"
            title={intl.formatMessage({
              id: 'tradeIn-options-subtitle'
            })}
          />
          <Row type="flex">
            <Col lg={8} xs={24}>
              <SearchWrapper>
                <Input
                  placeholder={intl.formatMessage({
                    id: 'tradeIn-options-optionsList-search-placeholder'
                  })}
                  onChange={handleChangeSearchWord}
                  suffix={<Icon type="search" style={{ color: 'rgb(28, 28, 28)' }} />}
                />
              </SearchWrapper>
            </Col>
          </Row>
          <Row type="flex">
            <Col span={24}>
              {optionsList && optionsList.equipments.length > 0 ? renderList() : <Empty />}
            </Col>
          </Row>
        </>
      )}
    </MainContentWrapper>
  )
}

Options.propTypes = {
  history: ReactRouterPropTypes.history.isRequired,
  intl: PropTypes.shape().isRequired,
  setWizards: PropTypes.func.isRequired,
  wizards: wizardsShape.isRequired,
  setTradeIn: PropTypes.func.isRequired,
  tradeIn: tradeInShape.isRequired,
  campaign: PropTypes.shape().isRequired,
  tradeInContext: PropTypes.shape().isRequired
}

export default withRouter(injectIntl(Options))
